import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import { Download, Eye } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  setBatchesTableHeaderTestId,
  setBatchesTableRowTestId,
  setBatchesTableStatusTestId,
  setBatchesTableTestId,
} from "./batches-table.utils";

export interface BatchData {
  id: string;
  createdAt: string;
  status: "completed" | "inProgress" | "error";
  orders: number;
  carrier: string;
  weight: number;
  manifestGenerated: boolean;
}

interface BatchesTableProps {
  data: BatchData[];
  selectedBatch: string | null;
  onSelectBatch: (batchId: string) => void;
  testId?: string;
}

const statusConfig = {
  completed: {
    label: "Completed",
    className:
      "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-950 dark:text-green-400",
  },
  inProgress: {
    label: "In Progress",
    className:
      "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-950 dark:text-blue-400",
  },
  error: {
    label: "Error",
    className:
      "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-950 dark:text-red-400",
  },
};

// Cell Components
function BatchIdCell({ id }: { id: string }) {
  return (
    <td className="px-6 py-4">
      <span className="font-mono text-sm font-medium">{id}</span>
    </td>
  );
}

function CreatedAtCell({ createdAt }: { createdAt: string }) {
  return (
    <td className="px-6 py-4">
      <span className="text-sm text-muted-foreground">{createdAt}</span>
    </td>
  );
}

function StatusCell({
  status,
  t,
}: {
  status: "completed" | "inProgress" | "error";
  t: (key: string) => string;
}) {
  const statusLabel = useMemo(() => {
    if (status === "completed") return t("views.batchesTable.status.completed");
    if (status === "inProgress")
      return t("views.batchesTable.status.inProgress");
    return t("views.batchesTable.status.error");
  }, [status, t]);

  return (
    <td className="px-6 py-4">
      {statusLabel ? (
        <Badge
          variant="secondary"
          className={statusConfig[status].className}
          data-testid={setBatchesTableStatusTestId(status)}
        >
          {statusLabel}
        </Badge>
      ) : null}
    </td>
  );
}

function OrdersCell({ orders }: { orders: number }) {
  return (
    <td className="px-6 py-4">
      <span className="text-sm font-medium">{orders}</span>
    </td>
  );
}

function CarrierCell({ carrier }: { carrier: string }) {
  return (
    <td className="px-6 py-4">
      <span className="text-sm">{carrier}</span>
    </td>
  );
}

function WeightCell({ weight }: { weight: number }) {
  return (
    <td className="px-6 py-4">
      <span className="text-sm text-muted-foreground">{weight} oz</span>
    </td>
  );
}

function ManifestCell({
  manifestGenerated,
  t,
}: {
  manifestGenerated: boolean;
  t: (key: string) => string;
}) {
  return (
    <td className="px-6 py-4">
      {manifestGenerated ? (
        <Badge
          variant="secondary"
          className="bg-primary/10 text-primary"
          data-testid={setBatchesTableTestId("manifest-generated")}
        >
          {t("views.batchesTable.manifestGenerated")}
        </Badge>
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      )}
    </td>
  );
}

function ActionsCell({
  manifestGenerated,
  t,
}: {
  manifestGenerated: boolean;
  t: (key: string) => string;
}) {
  return (
    <td className="px-6 py-4 text-right">
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Eye className="h-4 w-4" />
          <span className="sr-only">
            {t("views.batchesTable.tooltips.viewBatch")}
          </span>
        </Button>
        {manifestGenerated && (
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Download className="h-4 w-4" />
            <span className="sr-only">
              {t("views.batchesTable.tooltips.downloadManifest")}
            </span>
          </Button>
        )}
      </div>
    </td>
  );
}

// Row Component
interface BatchRowProps {
  batch: BatchData;
  isSelected: boolean;
  onSelect: () => void;
  t: (key: string) => string;
}

function BatchRow({ batch, isSelected, onSelect, t }: BatchRowProps) {
  return (
    <tr
      className={`hover:bg-muted/50 transition-colors cursor-pointer ${
        isSelected ? "bg-primary/5" : ""
      }`}
      onClick={onSelect}
      data-testid={setBatchesTableRowTestId(batch.id)}
    >
      <BatchIdCell id={batch.id} />
      <CreatedAtCell createdAt={batch.createdAt} />
      <StatusCell status={batch.status} t={t} />
      <OrdersCell orders={batch.orders} />
      <CarrierCell carrier={batch.carrier} />
      <WeightCell weight={batch.weight} />
      <ManifestCell manifestGenerated={batch.manifestGenerated} t={t} />
      <ActionsCell manifestGenerated={batch.manifestGenerated} t={t} />
    </tr>
  );
}

// Main Table Component
export function BatchesTable({
  data,
  selectedBatch,
  onSelectBatch,
  testId,
}: BatchesTableProps) {
  const { t } = useTranslation();
  return (
    <Card testId={testId ?? setBatchesTableTestId("container")}>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  data-testid={setBatchesTableHeaderTestId("batch-id")}
                >
                  {t("views.batchesTable.columns.batchId")}
                </th>
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  data-testid={setBatchesTableHeaderTestId("created")}
                >
                  {t("views.batchesTable.columns.created")}
                </th>
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  data-testid={setBatchesTableHeaderTestId("status")}
                >
                  {t("views.batchesTable.columns.status")}
                </th>
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  data-testid={setBatchesTableHeaderTestId("orders")}
                >
                  {t("views.batchesTable.columns.orders")}
                </th>
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  data-testid={setBatchesTableHeaderTestId("carrier")}
                >
                  {t("views.batchesTable.columns.carrier")}
                </th>
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  data-testid={setBatchesTableHeaderTestId("weight")}
                >
                  {t("views.batchesTable.columns.weight")}
                </th>
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  data-testid={setBatchesTableHeaderTestId("manifest")}
                >
                  {t("views.batchesTable.columns.manifest")}
                </th>
                <th
                  className="px-6 py-4 text-right text-sm font-semibold"
                  data-testid={setBatchesTableHeaderTestId("actions")}
                >
                  {t("views.batchesTable.columns.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((batch) => (
                <BatchRow
                  key={batch.id}
                  batch={batch}
                  isSelected={selectedBatch === batch.id}
                  onSelect={() => onSelectBatch(batch.id)}
                  t={t as (key: string) => string}
                />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
